/**
 * The four remuneration sections, as sentences.
 *
 * Extracted so the company panel and the contract preview say the same thing:
 * both describe the same settings, and wording kept in two places drifts.
 * Returns data rather than markup — each caller styles it its own way.
 */

const percentageLabel = (percentage) =>
  percentage !== undefined && percentage !== null
    ? `${percentage} %`
    : 'Non défini';

/** Entries sharing a rate are listed together, as the panel groups them. */
const groupByPercentage = (entries) => {
  const groups = {};
  (entries ?? []).forEach((entry) => {
    const key = String(entry.percentage ?? 'nc');
    if (!groups[key]) groups[key] = [];
    groups[key].push(entry);
  });
  return groups;
};

const overtimeLines = (overtimes) =>
  Object.values(groupByPercentage(overtimes)).map((group) => {
    const thresholds = group.map((entry) => `${entry.start}h`).join(' et ');
    return `Plus de ${thresholds} sur une semaine : ${percentageLabel(
      group[0]?.percentage
    )}`;
  });

const nightLines = (nightHours) =>
  Object.values(groupByPercentage(nightHours)).map((group) => {
    const ranges = group
      .map((entry) => `${entry.start}h et ${entry.end}h`)
      .join(' et ');
    return `Entre ${ranges} : ${percentageLabel(group[0]?.percentage)}`;
  });

/**
 * `sundayHours` and `holidayHours` are sub-documents, not booleans: an empty
 * one is still truthy, so presence is decided on the percentage being set.
 */
const isSet = (section) =>
  section?.percentage !== undefined && section?.percentage !== null;

export const remunerationSections = (companyInfo) => [
  {
    key: 'overtimes',
    title: 'Heures supplémentaires',
    isDefault: (companyInfo?.overtimes ?? []).length === 0,
    intro: "Majoration appliquée lorsqu'un intérimaire travaille :",
    bullets: overtimeLines(companyInfo?.overtimes),
  },
  {
    key: 'nightHours',
    title: 'Heures de nuit',
    isDefault: (companyInfo?.nightHours ?? []).length === 0,
    intro: "Majoration appliquée lorsqu'un intérimaire travaille :",
    bullets: nightLines(companyInfo?.nightHours),
  },
  {
    key: 'sundayHours',
    title: 'Heures du dimanche',
    isDefault: !isSet(companyInfo?.sundayHours),
    intro: isSet(companyInfo?.sundayHours)
      ? `Majoration appliquée lorsqu'un intérimaire travaille le dimanche : ${percentageLabel(
          companyInfo.sundayHours.percentage
        )}`
      : null,
    bullets: [],
  },
  {
    key: 'holidayHours',
    title: 'Heures jours fériés travaillés',
    isDefault: !isSet(companyInfo?.holidayHours),
    intro: isSet(companyInfo?.holidayHours)
      ? `Majoration appliquée lorsqu'un intérimaire travaille lors d'un jour férié : ${percentageLabel(
          companyInfo.holidayHours.percentage
        )}`
      : null,
    // Named by the server; absent when the referential could not resolve it.
    extra: companyInfo?.solidarityDayLabel
      ? `Jour de solidarité : ${companyInfo.solidarityDayLabel}`
      : null,
    bullets: [],
  },
];

export default remunerationSections;
